<script setup>
import { ref, onMounted } from 'vue';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { reportPeriodUnix } from 'dashboard/helper/reportPeriod';
import { useReportTimezone } from 'dashboard/composables/useReportTimezone';
import subDays from 'date-fns/subDays';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import ReportFilterPanel from './ReportFilterPanel.vue';
import {
  generateReportURLParams,
  parseReportURLParams,
} from '../helpers/reportFilterHelper';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import { useWorkspaceV2 } from 'dashboard/composables/useWorkspaceV2';
import { useShellPeriod } from '../composables/useShellPeriod';
import { rangeLabelKey } from '../helpers/reportsPeriod';

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },
  errorMessage: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['filterChange', 'retry']);

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { isWorkspaceV2 } = useWorkspaceV2();

const customDateRange = ref([subDays(new Date(), 6), new Date()]);
const selectedDateRange = ref(DATE_RANGE_TYPES.LAST_7_DAYS);
const businessHoursSelected = ref(false);

const selectedRangeLabel = computed(() =>
  t(rangeLabelKey(selectedDateRange.value))
);

const filterContext = computed(() => {
  const context = [selectedRangeLabel.value];
  if (businessHoursSelected.value) context.push(t('REPORT.BUSINESS_HOURS'));
  return context.join(' · ');
});

// Bordas do periodo no fuso da CONTA, nao no do navegador.
const { reportTimezone } = useReportTimezone();
const reportPeriod = computed(() =>
  reportPeriodUnix({
    dates: customDateRange.value,
    rangeType: selectedDateRange.value,
    timeZone: reportTimezone.value,
  })
);

const canReset = computed(
  () =>
    selectedDateRange.value !== DATE_RANGE_TYPES.LAST_7_DAYS ||
    businessHoursSelected.value
);

const updateURLParams = () => {
  const params = generateReportURLParams({
    from: reportPeriod.value.from,
    to: reportPeriod.value.to,
    businessHours: businessHoursSelected.value,
    range: selectedDateRange.value,
  });

  router.replace({ query: { ...params } });
};

const emitChange = () => {
  updateURLParams();
  emit('filterChange', {
    from: reportPeriod.value.from,
    to: reportPeriod.value.to,
    businessHours: businessHoursSelected.value,
  });
};

const onDateRangeChange = value => {
  const [startDate, endDate, rangeType] = value;
  customDateRange.value = [startDate, endDate];
  selectedDateRange.value = rangeType || DATE_RANGE_TYPES.CUSTOM_RANGE;
  emitChange();
};

// Dentro da casa dos relatorios o periodo vem do seletor unico da casca.
const shellPeriod = useShellPeriod(next => {
  if (
    next.range === selectedDateRange.value &&
    next.from === reportPeriod.value.from &&
    next.to === reportPeriod.value.to
  )
    return;
  onDateRangeChange([next.startDay, next.endDay, next.range]);
});

const onBusinessHoursToggle = () => {
  emitChange();
};

const resetFilters = () => {
  customDateRange.value = [subDays(new Date(), 6), new Date()];
  selectedDateRange.value = DATE_RANGE_TYPES.LAST_7_DAYS;
  businessHoursSelected.value = false;
  emitChange();
};

const initializeFromURL = () => {
  const urlParams = parseReportURLParams(route.query);

  // Set the range type first
  if (urlParams.range) {
    selectedDateRange.value = urlParams.range;
  }

  // Restore dates from URL if available
  if (urlParams.from && urlParams.to) {
    customDateRange.value = [
      new Date(urlParams.from * 1000),
      new Date(urlParams.to * 1000),
    ];
  }

  if (urlParams.businessHours) {
    businessHoursSelected.value = urlParams.businessHours;
  }
};

onMounted(() => {
  initializeFromURL();
  emitChange();
});
</script>

<template>
  <ReportFilterPanel
    :context="filterContext"
    :can-reset="canReset"
    :error-message="props.errorMessage"
    :disabled="disabled"
    @reset="resetFilters"
    @retry="emit('retry')"
  >
    <div
      class="flex flex-col justify-between gap-3 md:flex-row"
      :class="{ 'pointer-events-none opacity-50': disabled }"
    >
      <label
        v-if="!shellPeriod"
        :class="
          isWorkspaceV2
            ? 'flex flex-col gap-1.5 text-xs font-medium text-n-slate-11'
            : 'contents'
        "
      >
        <span v-if="isWorkspaceV2">{{ $t('REPORT.FILTER_PANEL.PERIOD') }}</span>
        <WootDatePicker
          v-model:date-range="customDateRange"
          v-model:range-type="selectedDateRange"
          @date-range-changed="onDateRangeChange"
        />
      </label>
      <label
        class="flex items-center text-sm whitespace-nowrap"
        :class="{ 'items-end gap-2 pb-1': isWorkspaceV2 }"
      >
        <span :class="{ 'mx-2': !isWorkspaceV2 }">
          {{ $t('REPORT.BUSINESS_HOURS') }}
        </span>
        <ToggleSwitch
          v-model="businessHoursSelected"
          @change="onBusinessHoursToggle"
        />
      </label>
    </div>
  </ReportFilterPanel>
</template>
